import React, { ChangeEventHandler, memo, useMemo, type FC } from "react";
import ColumnText from "./ColumnText";
import { Meal } from "@/app/nutrition/meal.enum";
import { Option } from "@/app/nutrition/option.enum";
import EllipsesMenuTableNutrition from "./EllipsesMenuTableNutrition";
import { InputWithUnit } from "./InputWithUnit";
import { portionToValue } from "@/app/nutrition/portion";
import { findClosestEquivalence } from "@/app/nutrition/equivalence";
import { EditorMode } from "@/app/nutrition/editor-mode.enum";

export type NutritionMealTableProductProps = {
  equivalences: Array<any>;
  editorMode: EditorMode;
  onPortionAltChange: (
    portion: string,
    meal: Meal,
    option: Option,
    index: number,
    editorMode: EditorMode
  ) => void;
  product: any;
  onChange: ChangeEventHandler<HTMLInputElement>;
  onDelete: (meal: Meal, option: Option, index: number) => void;
  meal: Meal;
  option: Option;
  index: number;
};

export const NutritionMealTableProduct: FC<NutritionMealTableProductProps> =
  memo(
    ({
      editorMode,
      index,
      meal,
      onChange,
      equivalences,
      onPortionAltChange,
      option,
      product,
      onDelete,
    }) => {
      const isNote = useMemo(
        () =>
          product?.product?.length > 0 && product?.product?.includes("Nota:"),
        [product]
      );

      useMemo(() => {
        const equivalence = findClosestEquivalence(
          equivalences,
          portionToValue(product.portion)
        );

        onPortionAltChange(
          equivalence?.portion,
          meal,
          option,
          index,
          EditorMode.TrainingDay
        );
      }, [equivalences, product.portion]);

      useMemo(() => {
        const equivalence = findClosestEquivalence(
          equivalences,
          portionToValue(product.portionRest)
        );

        onPortionAltChange(
          equivalence?.portion,
          meal,
          option,
          index,
          EditorMode.RestDay
        );
      }, [equivalences, product.portionRest]);

      return (
        <tr key={product.id}>
          <ColumnText title={product?.product} wrap />
          <td className={"whitespace-nowrap px-3 py-5 text-sm text-gray-500"}>
            {!isNote && editorMode === EditorMode.TrainingDay && (
              <div className="w-[100px]">
                <InputWithUnit
                  data-type="trainingDay"
                  data-index={index}
                  data-meal={meal}
                  data-option={option}
                  data-product={product.product}
                  value={product.portion}
                  onChange={onChange}
                  name={`nutrition.${meal}.[${option}].products[${index}].portion`}
                />
              </div>
            )}
            {!isNote && editorMode === EditorMode.RestDay && (
              <div className="w-[100px]">
                <InputWithUnit
                  data-type="restDay"
                  data-product={product.product}
                  data-index={index}
                  data-meal={meal}
                  data-option={option}
                  value={product.portionRest}
                  onChange={onChange}
                  name={`nutrition.${meal}.[${option}].products[${index}].portionRest`}
                />
              </div>
            )}
          </td>
          {!isNote && editorMode === EditorMode.TrainingDay && (
            <ColumnText title={product.portionAlt} />
          )}
          {!isNote && editorMode === EditorMode.RestDay && (
            <ColumnText title={product.portionRestAlt} />
          )}
          <td className="relative whitespace-nowrap py-5 pl-3 pr-4 text-sm font-medium sm:pr-0 flex justify-end mr-5">
            <EllipsesMenuTableNutrition
              onDelete={() => onDelete(meal, option, index)}
            />
          </td>
        </tr>
      );
    }
  );
